import React, { useRef, useState, useEffect } from 'react';
import doodleStyles from './Doodle.module.css';

const Doodle = ({ children, position, size = 120, theme = 'light', opacity, className, strokeW, draw = true }) => {
  const defaultOpacity = theme === 'light' ? 0.18 : 0.14;
  const strokeColor = theme === 'light' ? '#ffffff' : '#3B46D0';
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!draw) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [draw]);

  return (
    <div
      ref={ref}
      className={`${className || ''} ${draw && isVisible ? doodleStyles.drawIn : ''}`}
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 0,
        width: size,
        height: size,
        opacity: opacity || defaultOpacity,
        ...position,
      }}
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeW || "2"}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </svg>
    </div>
  );
};

/* ===== Doodle Library ===== */

export const CircuitTrace = (props) => (
  <Doodle {...props}>
    <line x1="10" y1="20" x2="50" y2="20" />
    <line x1="50" y1="20" x2="50" y2="45" />
    <line x1="50" y1="45" x2="85" y2="45" />
    <line x1="30" y1="55" x2="70" y2="55" />
    <line x1="70" y1="55" x2="70" y2="80" />
    <line x1="70" y1="80" x2="90" y2="80" />
    <circle cx="50" cy="20" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="50" cy="45" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="70" cy="55" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="70" cy="80" r="2.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const BrokenChain = (props) => (
  <Doodle {...props}>
    <ellipse cx="35" cy="50" rx="18" ry="12" />
    <ellipse cx="68" cy="50" rx="18" ry="12" />
    <line x1="52" y1="42" x2="56" y2="38" />
    <line x1="52" y1="58" x2="56" y2="62" />
  </Doodle>
);

export const ApiBrackets = (props) => (
  <Doodle {...props}>
    <path d="M30 25 Q20 25 20 35 L20 45 Q20 50 15 50 Q20 50 20 55 L20 65 Q20 75 30 75" />
    <path d="M70 25 Q80 25 80 35 L80 45 Q80 50 85 50 Q80 50 80 55 L80 65 Q80 75 70 75" />
    <line x1="45" y1="35" x2="55" y2="65" />
    <circle cx="38" cy="45" r="2" fill="currentColor" stroke="none" />
    <circle cx="62" cy="55" r="2" fill="currentColor" stroke="none" />
    <circle cx="50" cy="50" r="1.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const GearOutline = (props) => (
  <Doodle {...props}>
    <path d="M50 20 L54 28 L62 26 L60 34 L68 38 L62 44 L66 52 L58 52 L56 60 L50 54 L44 60 L42 52 L34 52 L38 44 L32 38 L40 34 L38 26 L46 28 Z" />
    <circle cx="50" cy="40" r="8" />
  </Doodle>
);

export const PipelineArrows = (props) => (
  <Doodle {...props}>
    <line x1="15" y1="30" x2="45" y2="30" />
    <polyline points="40,25 45,30 40,35" />
    <line x1="30" y1="50" x2="60" y2="50" />
    <polyline points="55,45 60,50 55,55" />
    <line x1="45" y1="70" x2="75" y2="70" />
    <polyline points="70,65 75,70 70,75" />
    <circle cx="15" cy="30" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="30" cy="50" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="45" cy="70" r="2.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const ConnectionDots = (props) => (
  <Doodle {...props}>
    {[25, 50, 75].map(y =>
      [25, 50, 75].map(x => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="3" fill="currentColor" stroke="none" />
      ))
    )}
    <line x1="25" y1="25" x2="50" y2="25" />
    <line x1="50" y1="25" x2="50" y2="50" />
    <line x1="50" y1="50" x2="75" y2="50" />
    <line x1="25" y1="50" x2="25" y2="75" />
    <line x1="25" y1="75" x2="50" y2="75" />
    <line x1="75" y1="50" x2="75" y2="75" />
  </Doodle>
);

export const ChartLine = (props) => (
  <Doodle {...props}>
    <line x1="15" y1="80" x2="15" y2="15" />
    <line x1="15" y1="80" x2="90" y2="80" />
    <polyline points="20,65 35,55 50,60 65,35 80,25" fill="none" />
    <circle cx="20" cy="65" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="35" cy="55" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="50" cy="60" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="65" cy="35" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="80" cy="25" r="2.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const CheckmarkCircuit = (props) => (
  <Doodle {...props}>
    <polyline points="25,55 40,70 75,30" strokeWidth="2.5" />
    <line x1="75" y1="30" x2="85" y2="30" />
    <line x1="85" y1="30" x2="85" y2="60" />
    <line x1="25" y1="55" x2="15" y2="55" />
    <line x1="15" y1="55" x2="15" y2="35" />
    <circle cx="85" cy="60" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="15" cy="35" r="2.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const AntennaSignal = (props) => (
  <Doodle {...props}>
    <line x1="50" y1="85" x2="50" y2="50" />
    <circle cx="50" cy="48" r="3" fill="currentColor" stroke="none" />
    <path d="M35 42 Q50 28 65 42" fill="none" />
    <path d="M25 36 Q50 16 75 36" fill="none" />
    <path d="M15 30 Q50 4 85 30" fill="none" />
  </Doodle>
);

export const TerminalCursor = (props) => (
  <Doodle {...props}>
    <polyline points="20,45 30,55 20,65" strokeWidth="2" />
    <line x1="38" y1="65" x2="60" y2="65" strokeWidth="2">
      <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" />
    </line>
    <line x1="20" y1="30" x2="70" y2="30" opacity="0.4" />
    <line x1="20" y1="22" x2="55" y2="22" opacity="0.3" />
  </Doodle>
);

export const CloudOutline = (props) => (
  <Doodle {...props}>
    <path d="M25 65 Q10 65 10 52 Q10 40 22 38 Q24 25 40 25 Q52 25 56 35 Q60 30 68 33 Q80 36 80 48 Q90 48 90 58 Q90 65 80 65 Z" />
    <line x1="35" y1="72" x2="35" y2="80" strokeDasharray="3 3" />
    <line x1="50" y1="72" x2="50" y2="85" strokeDasharray="3 3" />
    <line x1="65" y1="72" x2="65" y2="78" strokeDasharray="3 3" />
  </Doodle>
);

export const ServerRack = (props) => (
  <Doodle {...props}>
    <rect x="20" y="15" width="60" height="70" rx="4" />
    <line x1="20" y1="33" x2="80" y2="33" />
    <line x1="20" y1="51" x2="80" y2="51" />
    <line x1="20" y1="69" x2="80" y2="69" />
    <circle cx="70" cy="24" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="70" cy="42" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="70" cy="60" r="2.5" fill="currentColor" stroke="none" />
    <line x1="30" y1="24" x2="55" y2="24" />
    <line x1="30" y1="42" x2="50" y2="42" />
    <line x1="30" y1="60" x2="45" y2="60" />
  </Doodle>
);

export const ShieldLock = (props) => (
  <Doodle {...props}>
    <path d="M50 10 L85 25 L85 55 Q85 80 50 92 Q15 80 15 55 L15 25 Z" />
    <rect x="38" y="45" width="24" height="20" rx="3" />
    <path d="M42 45 L42 38 Q42 30 50 30 Q58 30 58 38 L58 45" fill="none" />
    <circle cx="50" cy="56" r="2.5" fill="currentColor" stroke="none" />
  </Doodle>
);

export const DatabaseCylinder = (props) => (
  <Doodle {...props}>
    <ellipse cx="50" cy="25" rx="30" ry="10" />
    <line x1="20" y1="25" x2="20" y2="75" />
    <line x1="80" y1="25" x2="80" y2="75" />
    <ellipse cx="50" cy="75" rx="30" ry="10" />
    <path d="M20 45 Q50 55 80 45" strokeDasharray="4 3" />
    <path d="M20 60 Q50 70 80 60" strokeDasharray="4 3" />
  </Doodle>
);

export const CodeTags = (props) => (
  <Doodle {...props}>
    <polyline points="35,25 15,50 35,75" strokeWidth="2.5" />
    <polyline points="65,25 85,50 65,75" strokeWidth="2.5" />
    <line x1="55" y1="20" x2="45" y2="80" strokeWidth="2" />
  </Doodle>
);

export const Microchip = (props) => (
  <Doodle {...props}>
    <rect x="30" y="30" width="40" height="40" rx="3" />
    <rect x="40" y="40" width="20" height="20" rx="2" />
    {/* Top pins */}
    <line x1="38" y1="30" x2="38" y2="18" />
    <line x1="50" y1="30" x2="50" y2="18" />
    <line x1="62" y1="30" x2="62" y2="18" />
    {/* Bottom pins */}
    <line x1="38" y1="70" x2="38" y2="82" />
    <line x1="50" y1="70" x2="50" y2="82" />
    <line x1="62" y1="70" x2="62" y2="82" />
    {/* Left pins */}
    <line x1="30" y1="38" x2="18" y2="38" />
    <line x1="30" y1="50" x2="18" y2="50" />
    <line x1="30" y1="62" x2="18" y2="62" />
    {/* Right pins */}
    <line x1="70" y1="38" x2="82" y2="38" />
    <line x1="70" y1="50" x2="82" y2="50" />
    <line x1="70" y1="62" x2="82" y2="62" />
  </Doodle>
);

export const DataFlow = (props) => (
  <Doodle {...props}>
    <circle cx="15" cy="50" r="6" />
    <circle cx="50" cy="20" r="6" />
    <circle cx="50" cy="80" r="6" />
    <circle cx="85" cy="50" r="6" />
    <line x1="21" y1="47" x2="44" y2="23" />
    <line x1="21" y1="53" x2="44" y2="77" />
    <line x1="56" y1="23" x2="79" y2="47" />
    <line x1="56" y1="77" x2="79" y2="53" />
    <polyline points="76,44 79,47 76,50" />
    <polyline points="76,47 79,50 76,53" />
  </Doodle>
);

export const WifiSignal = (props) => (
  <Doodle {...props}>
    <circle cx="50" cy="75" r="4" fill="currentColor" stroke="none" />
    <path d="M30 62 Q50 48 70 62" fill="none" strokeWidth="2.5" />
    <path d="M18 50 Q50 30 82 50" fill="none" strokeWidth="2.5" />
    <path d="M8 38 Q50 12 92 38" fill="none" strokeWidth="2.5" />
  </Doodle>
);

export const BinaryCode = (props) => (
  <Doodle {...props}>
    <text x="10" y="22" fill="currentColor" stroke="none" fontSize="12" fontFamily="monospace" opacity="0.7">01101</text>
    <text x="25" y="38" fill="currentColor" stroke="none" fontSize="12" fontFamily="monospace" opacity="0.5">10010</text>
    <text x="15" y="54" fill="currentColor" stroke="none" fontSize="12" fontFamily="monospace" opacity="0.8">11001</text>
    <text x="30" y="70" fill="currentColor" stroke="none" fontSize="12" fontFamily="monospace" opacity="0.4">01110</text>
    <text x="10" y="86" fill="currentColor" stroke="none" fontSize="12" fontFamily="monospace" opacity="0.6">10101</text>
  </Doodle>
);

export const NetworkNodes = (props) => (
  <Doodle {...props}>
    <circle cx="50" cy="50" r="8" strokeWidth="2" />
    <circle cx="15" cy="20" r="5" />
    <circle cx="85" cy="20" r="5" />
    <circle cx="15" cy="80" r="5" />
    <circle cx="85" cy="80" r="5" />
    <circle cx="50" cy="10" r="4" />
    <circle cx="50" cy="90" r="4" />
    <line x1="20" y1="23" x2="42" y2="45" />
    <line x1="80" y1="23" x2="58" y2="45" />
    <line x1="20" y1="77" x2="42" y2="55" />
    <line x1="80" y1="77" x2="58" y2="55" />
    <line x1="50" y1="14" x2="50" y2="42" />
    <line x1="50" y1="58" x2="50" y2="86" />
  </Doodle>
);

export default Doodle;
